/**
 * KS-PF4 — Connectors Page
 *
 * Manages connector instances, triggers syncs, and shows sync run history.
 * Only ORG_ADMIN can create / update / disable connectors.
 * ORG_ADMIN and KNOWLEDGE_STEWARD can trigger syncs.
 * All roles can read.
 */
import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { session } from '@/lib/session';

// ─── Status badge helpers ─────────────────────────────────────────────────────

const statusColors = {
  ACTIVE:    { bg: '#052e16', border: '#16a34a', text: '#4ade80' },
  DISABLED:  { bg: '#1c1917', border: '#78716c', text: '#a8a29e' },
  ERROR:     { bg: '#450a0a', border: '#dc2626', text: '#f87171' },
  COMPLETED: { bg: '#052e16', border: '#16a34a', text: '#4ade80' },
  FAILED:    { bg: '#450a0a', border: '#dc2626', text: '#f87171' },
  INCOMPLETE:{ bg: '#431407', border: '#ea580c', text: '#fb923c' },
  RUNNING:   { bg: '#1e3a5f', border: '#3b82f6', text: '#93c5fd' },
  PENDING:   { bg: '#1c1917', border: '#78716c', text: '#a8a29e' },
  IDLE:      { bg: '#1c1917', border: '#44403c', text: '#78716c' },
};

function StatusBadge({ label }) {
  const colors = statusColors[label] || statusColors.IDLE;
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '11px',
        fontWeight: 600,
        letterSpacing: '0.04em',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        fontFamily: 'monospace',
      }}
    >
      {label}
    </span>
  );
}

// ─── Create Connector Modal ───────────────────────────────────────────────────

const VALID_SCENARIOS = [
  'new_object','unchanged_object','modified_object','removed_object',
  'duplicate_filename','pagination','incomplete_discovery','timeout',
  'rate_limit','authorization_failure','malformed_object','oversized_object',
  'duplicate_revision','interrupted_sync',
];

function CreateConnectorModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    display_name: '',
    description: '',
    scenario: 'new_object',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const resp = await api.post('/connectors', {
        connector_type: 'reference',
        display_name: form.display_name,
        description: form.description,
        config: { scenario: form.scenario },
      });
      onCreated(resp.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create connector.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="create-connector-modal"
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,0.7)', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div style={{
        background: '#111',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '28px',
        width: '480px',
        maxWidth: '95vw',
      }}>
        <h2 style={{ color: '#e5e7eb', fontSize: '16px', fontWeight: 700, marginBottom: '20px' }}>
          Create Reference Connector
        </h2>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '6px' }}>
              Display Name *
            </label>
            <input
              data-testid="connector-name-input"
              required
              value={form.display_name}
              onChange={e => setForm(f => ({ ...f, display_name: e.target.value }))}
              style={{
                width: '100%', padding: '8px 10px', background: '#1a1a1a',
                border: '1px solid #333', borderRadius: '4px', color: '#e5e7eb',
                fontSize: '13px',
              }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '6px' }}>
              Description *
            </label>
            <input
              data-testid="connector-description-input"
              required
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              style={{
                width: '100%', padding: '8px 10px', background: '#1a1a1a',
                border: '1px solid #333', borderRadius: '4px', color: '#e5e7eb',
                fontSize: '13px',
              }}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', color: '#9ca3af', fontSize: '12px', marginBottom: '6px' }}>
              Reference Scenario
            </label>
            <select
              data-testid="connector-scenario-select"
              value={form.scenario}
              onChange={e => setForm(f => ({ ...f, scenario: e.target.value }))}
              style={{
                width: '100%', padding: '8px 10px', background: '#1a1a1a',
                border: '1px solid #333', borderRadius: '4px', color: '#e5e7eb',
                fontSize: '13px',
              }}
            >
              {VALID_SCENARIOS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          {error && (
            <div data-testid="connector-create-error" style={{ color: '#f87171', fontSize: '12px', marginBottom: '14px' }}>
              {error}
            </div>
          )}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '8px 16px', background: 'transparent',
                border: '1px solid #333', borderRadius: '4px',
                color: '#9ca3af', fontSize: '13px', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              data-testid="connector-create-submit"
              type="submit"
              disabled={loading}
              style={{
                padding: '8px 16px', background: '#1d4ed8',
                border: 'none', borderRadius: '4px',
                color: '#fff', fontSize: '13px', cursor: 'pointer',
                opacity: loading ? 0.6 : 1,
              }}
            >
              {loading ? 'Creating...' : 'Create Connector'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Sync Run Detail Panel ────────────────────────────────────────────────────

function SyncRunDetail({ run }) {
  if (!run) return null;
  return (
    <div
      data-testid="sync-run-detail"
      style={{
        background: '#0a0a0a', border: '1px solid #222',
        borderRadius: '6px', padding: '16px', marginTop: '12px',
      }}
    >
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <StatusBadge label={run.status} />
        <span style={{ color: '#6b7280', fontSize: '12px' }}>
          Run ID: <code style={{ color: '#9ca3af' }}>{run.run_id}</code>
        </span>
        <span style={{ color: '#6b7280', fontSize: '12px' }}>
          Started: {new Date(run.started_at).toLocaleString()}
        </span>
        {run.completed_at && (
          <span style={{ color: '#6b7280', fontSize: '12px' }}>
            Completed: {new Date(run.completed_at).toLocaleString()}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '12px' }}>
        {[
          ['Discovered', run.objects_discovered],
          ['Ingested',   run.objects_ingested],
          ['Updated',    run.objects_updated],
          ['Skipped',    run.objects_skipped],
          ['Failed',     run.objects_failed],
          ['Removed↑',   run.objects_removed_upstream],
        ].map(([label, val]) => (
          <div key={label} style={{ textAlign: 'center' }}>
            <div style={{ color: '#e5e7eb', fontSize: '18px', fontWeight: 700 }}>{val ?? 0}</div>
            <div style={{ color: '#6b7280', fontSize: '10px', letterSpacing: '0.05em' }}>{label}</div>
          </div>
        ))}
      </div>
      {run.error_message && (
        <div style={{
          background: '#1c0000', border: '1px solid #7f1d1d',
          borderRadius: '4px', padding: '8px 10px',
          color: '#fca5a5', fontSize: '12px', marginBottom: '10px',
        }}>
          {run.error_message}
        </div>
      )}
      {run.events && run.events.length > 0 && (
        <div>
          <div style={{ color: '#6b7280', fontSize: '11px', marginBottom: '6px', letterSpacing: '0.06em' }}>
            OBJECT EVENTS
          </div>
          {run.events.map((ev, i) => (
            <div
              key={i}
              data-testid={`sync-event-${i}`}
              style={{
                display: 'flex', gap: '12px', alignItems: 'center',
                padding: '5px 0', borderBottom: '1px solid #1a1a1a',
                fontSize: '12px',
              }}
            >
              <StatusBadge label={ev.action} />
              <code style={{ color: '#9ca3af', flex: 1 }}>{ev.external_id}</code>
              {ev.artifact_id && (
                <span style={{ color: '#4b5563', fontSize: '11px' }}>
                  artifact: {ev.artifact_id.slice(0, 8)}…
                </span>
              )}
              {ev.admission_state && (
                <StatusBadge label={ev.admission_state} />
              )}
              {ev.reason && (
                <span style={{ color: '#4b5563', fontSize: '11px' }}>{ev.reason}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Connector Card ───────────────────────────────────────────────────────────

function ConnectorCard({ connector, role, onRefresh }) {
  const [syncing, setSyncing]       = useState(false);
  const [disabling, setDisabling]   = useState(false);
  const [syncRuns, setSyncRuns]     = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [showRuns, setShowRuns]     = useState(false);
  const [error, setError]           = useState(null);

  const canManage = role === 'ORG_ADMIN';
  const canSync   = role === 'ORG_ADMIN' || role === 'KNOWLEDGE_STEWARD';

  const triggerSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      await api.post(`/connectors/${connector.connector_id}/trigger-sync`);
      onRefresh();
      // Auto-load runs after sync
      await loadRuns();
    } catch (err) {
      setError(err.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const disableConnector = async () => {
    if (!window.confirm('Disable this connector? All governed data will be preserved.')) return;
    setDisabling(true);
    setError(null);
    try {
      await api.post(`/connectors/${connector.connector_id}/disable`);
      onRefresh();
    } catch (err) {
      setError(err.response?.data?.detail || 'Disable failed.');
    } finally {
      setDisabling(false);
    }
  };

  const loadRuns = async () => {
    try {
      const resp = await api.get(`/connectors/${connector.connector_id}/sync-runs`);
      setSyncRuns(resp.data);
      setShowRuns(true);
    } catch (_) {
      setSyncRuns([]);
    }
  };

  const toggleRuns = () => {
    if (!showRuns) loadRuns();
    else setShowRuns(false);
  };

  return (
    <div
      data-testid={`connector-card-${connector.connector_id}`}
      style={{
        background: '#0d0d0d',
        border: '1px solid #1f1f1f',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '12px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '4px' }}>
            <span data-testid="connector-display-name" style={{ color: '#e5e7eb', fontSize: '15px', fontWeight: 600 }}>
              {connector.display_name}
            </span>
            <StatusBadge label={connector.status} />
            <StatusBadge label={connector.current_sync_status || 'IDLE'} />
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px', marginBottom: '6px' }}>
            {connector.description}
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#4b5563' }}>
            <span>Type: <code style={{ color: '#6b7280' }}>{connector.connector_type}</code></span>
            <span>Scenario: <code style={{ color: '#6b7280' }}>{connector.config?.scenario}</code></span>
            {connector.last_successful_sync_at && (
              <span>Last sync: {new Date(connector.last_successful_sync_at).toLocaleString()}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '16px' }}>
          <button
            data-testid="view-sync-runs-btn"
            onClick={toggleRuns}
            style={{
              padding: '6px 12px', background: 'transparent',
              border: '1px solid #333', borderRadius: '4px',
              color: '#9ca3af', fontSize: '12px', cursor: 'pointer',
            }}
          >
            {showRuns ? 'Hide Runs' : 'Sync Runs'}
          </button>
          {canSync && connector.status === 'ACTIVE' && (
            <button
              data-testid="trigger-sync-btn"
              onClick={triggerSync}
              disabled={syncing || connector.current_sync_status === 'RUNNING'}
              style={{
                padding: '6px 12px', background: '#1d4ed8',
                border: 'none', borderRadius: '4px',
                color: '#fff', fontSize: '12px', cursor: 'pointer',
                opacity: syncing || connector.current_sync_status === 'RUNNING' ? 0.5 : 1,
              }}
            >
              {syncing ? 'Syncing...' : 'Trigger Sync'}
            </button>
          )}
          {canManage && connector.status === 'ACTIVE' && (
            <button
              data-testid="disable-connector-btn"
              onClick={disableConnector}
              disabled={disabling}
              style={{
                padding: '6px 12px', background: 'transparent',
                border: '1px solid #7f1d1d', borderRadius: '4px',
                color: '#f87171', fontSize: '12px', cursor: 'pointer',
                opacity: disabling ? 0.5 : 1,
              }}
            >
              {disabling ? 'Disabling...' : 'Disable'}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div data-testid="connector-card-error" style={{ color: '#f87171', fontSize: '12px', marginTop: '8px' }}>
          {error}
        </div>
      )}

      {showRuns && (
        <div style={{ marginTop: '16px' }}>
          <div style={{ color: '#6b7280', fontSize: '11px', letterSpacing: '0.06em', marginBottom: '8px' }}>
            SYNC RUN HISTORY
          </div>
          {!syncRuns && <div style={{ color: '#4b5563', fontSize: '12px' }}>Loading...</div>}
          {syncRuns && syncRuns.length === 0 && (
            <div style={{ color: '#4b5563', fontSize: '12px' }}>No sync runs yet.</div>
          )}
          {syncRuns && syncRuns.map(run => (
            <div
              key={run.run_id}
              data-testid={`sync-run-row-${run.run_id}`}
              onClick={() => setSelectedRun(selectedRun?.run_id === run.run_id ? null : run)}
              style={{
                display: 'flex', gap: '12px', alignItems: 'center',
                padding: '8px 10px',
                background: selectedRun?.run_id === run.run_id ? '#111' : 'transparent',
                borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
                border: selectedRun?.run_id === run.run_id ? '1px solid #333' : '1px solid transparent',
              }}
            >
              <StatusBadge label={run.status} />
              <span style={{ color: '#9ca3af' }}>{new Date(run.started_at).toLocaleString()}</span>
              <span style={{ color: '#6b7280' }}>
                ↓{run.objects_discovered} +{run.objects_ingested} ~{run.objects_skipped} ✗{run.objects_failed}
              </span>
              {run.objects_removed_upstream > 0 && (
                <span style={{ color: '#fb923c', fontSize: '11px' }}>
                  {run.objects_removed_upstream} removed↑
                </span>
              )}
            </div>
          ))}
          <SyncRunDetail run={selectedRun} />
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Connectors() {
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const user = session.getUser();
  const role = user?.role;

  const canManage = role === 'ORG_ADMIN';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get('/connectors');
      setConnectors(resp.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load connectors.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div
      data-testid="connectors-page"
      style={{ padding: '28px 32px', maxWidth: '900px', margin: '0 auto' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
        <div>
          <h1 style={{ color: '#e5e7eb', fontSize: '22px', fontWeight: 700, margin: 0 }}>
            Connectors
          </h1>
          <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>
            Governed connectors provide information. They do not provide authority.
            Connector-derived content must pass normal review before evidence acceptance or canon promotion.
          </p>
        </div>
        {canManage && (
          <button
            data-testid="create-connector-btn"
            onClick={() => setShowCreate(true)}
            style={{
              padding: '8px 16px', background: '#1d4ed8',
              border: 'none', borderRadius: '6px',
              color: '#fff', fontSize: '13px', cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            + Add Connector
          </button>
        )}
      </div>

      {/* Governance notice */}
      <div style={{
        background: '#0f1f14',
        border: '1px solid #14532d',
        borderRadius: '6px',
        padding: '12px 16px',
        marginBottom: '24px',
        fontSize: '12px',
        color: '#86efac',
      }}>
        <strong>KS-PF4 Governance:</strong> Connector-derived artifacts enter the CANDIDATE admission state.
        They are never auto-promoted to CANON_LOCKED. Upstream deletion does not auto-delete KYRDROS data.
        Concurrent syncs are blocked per connector.
      </div>

      {error && (
        <div data-testid="connectors-error" style={{ color: '#f87171', fontSize: '13px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div data-testid="connectors-loading" style={{ color: '#4b5563', fontSize: '13px' }}>
          Loading connectors...
        </div>
      ) : connectors.length === 0 ? (
        <div data-testid="connectors-empty" style={{
          textAlign: 'center', padding: '48px 0',
          color: '#4b5563', fontSize: '13px',
        }}>
          {canManage
            ? 'No connectors configured. Click "+ Add Connector" to create one.'
            : 'No connectors have been configured for your organization.'}
        </div>
      ) : (
        <div data-testid="connectors-list">
          {connectors.map(c => (
            <ConnectorCard
              key={c.connector_id}
              connector={c}
              role={role}
              onRefresh={load}
            />
          ))}
        </div>
      )}

      {showCreate && (
        <CreateConnectorModal
          onClose={() => setShowCreate(false)}
          onCreated={(newConnector) => {
            setConnectors(prev => [newConnector, ...prev]);
            setShowCreate(false);
          }}
        />
      )}
    </div>
  );
}
